// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import loadCustomerPartNumbers from "@steiner/App/Handlers/CustomerPartNumber/LoadCustomerPartNumbers";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { XNGAGE_ELEMENTS } from "@steiner/XDL/constants";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => ({
    pagination: state.xCustomerPartNumbers.pagination,
});

const mapDispatchToProps = {
    loadCustomerPartNumbers,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CustomerPartNumbersPaginationStyles {
    pagination?: PaginationPresentationProps;
}

export const customerPartNumbersPaginationStyles: CustomerPartNumbersPaginationStyles = {};

const styles = customerPartNumbersPaginationStyles;

const CustomerPartNumbersPagination = ({ pagination, loadCustomerPartNumbers }: Props) => {
    const [shouldScrollToTop, setShouldScrollToTop] = useState(false);
    useEffect(() => {
        if (shouldScrollToTop) {
            window.scrollTo(0, 0);
            setShouldScrollToTop(false);
        }
    }, [pagination?.page]);

    const changePage = (newPageIndex: number) => {
        setShouldScrollToTop(true);
        loadCustomerPartNumbers({
            page: newPageIndex,
            pageSize: pagination?.pageSize,
        });
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        loadCustomerPartNumbers({
            page: 1,
            pageSize: newPageSize,
        });
    };

    if (!pagination || !pagination.totalItemCount) {
        return null;
    }

    return (
        <Pagination
            {...styles.pagination}
            resultsCount={pagination.totalItemCount}
            currentPage={pagination.page}
            resultsPerPage={pagination.pageSize}
            resultsPerPageOptions={pagination.pageSizeOptions}
            onChangePage={changePage}
            onChangeResultsPerPage={changeResultsPerPage}
            pageSizeCookie="customerPartNumbers-PageSize"
            data-test-selector="customerPartNumbers_pagination"
            prevPage={pagination.prevPageUri}
            nextPage={pagination.nextPageUri}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CustomerPartNumbersPagination),
    definition: {
        group: "Customer Part Number" as any,
        displayName: "Customer Part Number Pagination",
        allowedContexts: ["CustomerPartNumberPage"],
    },
};

export default widgetModule;
